import { useEffect, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { api } from "@/api";
import { Loading } from "@/deps/ui-library";

type Kind = {
  list: string;
  detail: string;
  find: (number: string) => Promise<Array<{ id: string; number: string | null }>>;
};

/** Which list a document number belongs to, by the prefix its series gives it. */
const KINDS: Record<string, Kind> = {
  INV: {
    list: "/sales/invoices",
    detail: "/sales/invoices/$id",
    find: async (q) => (await api.invoices.list({ q })).items,
  },
  QUO: {
    list: "/sales/quotations",
    detail: "/sales/quotations/$id",
    find: async (q) => (await api.quotations.list({ q })).items,
  },
  CN: {
    list: "/sales/credit-notes",
    detail: "/sales/credit-notes/$id",
    find: async (q) => (await api.creditNotes.list({ q })).items,
  },
  RCPT: {
    list: "/sales/receipts",
    detail: "/sales/receipts/$id",
    find: async (q) => (await api.receipts.list({ q })).items,
  },
  PO: {
    list: "/purchases/orders",
    detail: "/purchases/orders/$id",
    find: async (q) => (await api.purchaseOrders.list({ q })).items,
  },
  BILL: {
    list: "/purchases/bills",
    detail: "/purchases/bills/$id",
    find: async (q) => (await api.bills.list({ q })).items,
  },
  DN: {
    list: "/purchases/debit-notes",
    detail: "/purchases/debit-notes/$id",
    find: async (q) => (await api.debitNotes.list({ q })).items,
  },
  PMT: {
    list: "/purchases/payments",
    detail: "/purchases/payments/$id",
    find: async (q) => (await api.payments.list({ q })).items,
  },
  JV: {
    list: "/accounting/journals",
    detail: "/accounting/journals/$id",
    find: async (q) => (await api.journals.list({ q })).items,
  },
  XFER: {
    list: "/banking/transfers",
    detail: "/banking/transfers/$id",
    find: async (q) => (await api.fundTransfers.list({ q })).items,
  },
};

/**
 * `/doc/INV-0231` opens that document from anywhere: a ledger line, an analysis table, a message.
 * A number nobody can resolve opens its list, searched for that number, rather than a dead end.
 */
export function DocumentLinkPage() {
  const { number } = useParams({ strict: false }) as { number: string };
  const navigate = useNavigate();
  const [unknown, setUnknown] = useState(false);

  useEffect(() => {
    const kind = KINDS[number.split("-")[0]?.toUpperCase() ?? ""];
    if (!kind) {
      setUnknown(true);
      return;
    }
    let live = true;
    const toList = () =>
      void navigate({ to: kind.list as never, search: { q: number } as never, replace: true });
    kind
      .find(number)
      .then((found) => {
        if (!live) return;
        const exact = found.find((row) => row.number === number);
        if (exact) {
          void navigate({
            to: kind.detail as never,
            params: { id: exact.id } as never,
            replace: true,
          });
        } else {
          toList();
        }
      })
      .catch(() => {
        if (live) toList();
      });
    return () => {
      live = false;
    };
  }, [navigate, number]);

  if (unknown) {
    return <p className="text-muted-foreground text-sm">No document is numbered {number}.</p>;
  }
  return <Loading />;
}
